function getApiBackendUrl(): string | undefined {
  if (typeof window !== 'undefined' && window.env && typeof window.env === 'object') {
    return (window.env as unknown as Record<string, string | undefined>).REACT_APP_API_BASE_URL;
  }
  return undefined;
}

export class ApiError extends Error {
  status: number;
  body?: any;
  constructor(message: string, status: number, body?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

function buildUrl(pathOrUrl: string) {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;

  const apiBaseUrl = getApiBackendUrl();
  if (!apiBaseUrl) {
    // No base URL configured: use the path as-is (relative URL, served by the same origin)
    return pathOrUrl;
  }

  const base = apiBaseUrl.replace(/\/+$/,'');
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}

function buildHeaders(token?: string, extra?: HeadersInit) {
  const normalizedHeaders = extra ? Object.fromEntries(new Headers(extra).entries()) : {};
  const headers: Record<string,string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...normalizedHeaders,
  };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return headers;
}

function logApiRequest(details: {
  method: string;
  pathOrUrl: string;
  url: string;
  token?: string;
}) {
  const { headers: _ignoredHeaders, ...initWithoutHeaders } = details.init ?? {};

  console.info('Frontend API request', {
    method: details.method,
    pathOrUrl: details.pathOrUrl,
    url: details.url,
    token: details.token ? '[redacted]' : undefined,
  });
}

function logApiRequestError(details: {
  method: string;
  url: string;
  status?: number;
  message: string;
  body?: unknown;
  error?: unknown;
}) {
  console.error('Frontend API request failed', details);
}

async function request<T>(method: string, pathOrUrl: string, token?: string, init?: RequestInit, body?: any): Promise<T> {
  const url = buildUrl(pathOrUrl);
  const headers = buildHeaders(token, init?.headers);
  const fetchInit: RequestInit = {
    ...init,
    method,
    headers,
  };

  if (body !== undefined) {
    try {
      fetchInit.body = JSON.stringify(body);
    } catch (_) {
      // fall back to raw body
      fetchInit.body = body as any;
    }
  }

  logApiRequest({
    method,
    pathOrUrl,
    url,
    token,
  });

  let response: Response;
  try {
    response = await fetch(url, fetchInit);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network request failed';
    logApiRequestError({
      method,
      url,
      message,
      error,
    });
    throw error;
  }

  const text = await response.text().catch(() => '');
  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    let parsed: any = undefined;
    if (contentType.includes('application/json') && text) {
      try { parsed = JSON.parse(text); } catch {};
    }
    const message = parsed?.message || text || `Request failed with status ${response.status}`;
    logApiRequestError({
      method,
      url,
      status: response.status,
      message,
      body: parsed ?? text,
    });
    throw new ApiError(message, response.status, parsed ?? text);
  }

  if (contentType.includes('application/json')) {
    return JSON.parse(text) as T;
  }

  // not json
  return (text as unknown) as T;
}

export async function apiGet<T>(pathOrUrl: string, token?: string, init?: RequestInit): Promise<T> {
  return request<T>('GET', pathOrUrl, token, init);
}

export async function apiPost<T>(pathOrUrl: string, token?: string, body?: any, init?: RequestInit): Promise<T> {
  return request<T>('POST', pathOrUrl, token, init, body);
}
